import { html } from '@adobe/edex/ui/edex-lit';
import { AxiosError } from 'axios';
import { unsafeHTML } from '@adobe/edex/ui/edex-lit';

import { $t } from '@adobe/edex/ui/shared/config/i18n';
import {
  AuthDialogPayload,
  DialogState,
  DownloadFileDialogPayload,
  ExportedDialogPayload,
  ExportType,
  MarshalledResource,
  PostAuth,
  ProductHelpfulInfo,
  SendToDriveDialogPayload,
} from '@adobe/edex/ui/types';
import '@adobe/edex/ui/components/shared/card/card';
import '@adobe/edex/ui/components/resource/download-local/download-local';
import '@adobe/edex/ui/components/resource/save-to-gd/save-to-gd';
import '@adobe/edex/ui/components/resource/save-to-od/save-to-od';
import '@adobe/edex/ui/components/resource/resource-header-toolbar/resource-header-toolbar';
import '@adobe/edex/ui/shared/components/resource-card/resource-card';
import '@adobe/edex/ui/components/resource/resource-action-bar/resource-action-bar';

import {
  closeDialog,
  fetchRelatedResources,
  fetchResource,
  openDialog,
  rateResource,
  saveResource,
} from '@adobe/edex/ui/store/routines';
import {
  selectDialog,
  selectLocationSearch,
  selectResource,
  selectResourceError,
  selectResourceLoading,
} from '@adobe/edex/ui/store/selectors';
import { RouterPageElement } from '@adobe/edex/ui/components/base.element';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { AdobeAnalyticsEvent, EdexDomID } from '@adobe/edex/ui/shared/constants';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-checkmark-circle-outline';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-right';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-close';
import { Actid, ADOBE_FOR_EDUCATION_ID, AnalyticsEvent, IS_TEST } from '@adobe/edex/ui/constants';
import {
  delay,
  formatDocumentTitle,
  getResourceAssetCount,
  getResourceShortDescription,
  isCCXTemplateOrProjectUrl,
  isNativeShareApplicable,
  processSEO,
  unsafeHTMLtext,
} from '@adobe/edex/ui/shared/utils';
import { FetchRelatedResourcesPayload } from '@adobe/edex/ui/types/payload';
import { EdexSEO } from '@adobe/edex/ui/shared/types/ui';
import { EdexState } from '@adobe/edex/ui/store';
import { router } from '@adobe/edex/ui/config/router';
import { getResizedImageURL } from '@adobe/edex/ui/shared/utils/imageResize';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';
import { isEmpty } from 'lodash-es';
import { FullStoryUtils } from '@adobe/edex/ui/shared/config/fullstory';

const HELPFUL_INFORMATION_IMAGE_WIDTH = 688;

export class ResourceBase extends RouterPageElement {
  static get properties() {
    return {
      ...super.properties,
      vanityURL: { type: String },
      builderPreview: { type: Boolean },
      resource: { type: Object },
      dialog: { attribute: false },
      error: { attribute: false },
      isResourceLoading: { attribute: false },
      locationSearch: { type: String },
      isMobileViewToolbar: { attribute: true },
      isMobileUserAgent: { attribute: true },
    };
  }
  vanityURL: string;
  builderPreview = false;
  resource: MarshalledResource;
  error: AxiosError;
  isResourceLoading = false;
  dialog: DialogState<DownloadFileDialogPayload & ExportedDialogPayload & AuthDialogPayload & SendToDriveDialogPayload>;
  analyticsViewEvent: AnalyticsEvent;
  actions = {
    fetchResource: fetchResource.trigger,
    fetchRelatedResource: fetchRelatedResources.trigger,
    rateResource: rateResource.trigger,
    saveResource: saveResource.trigger,
    openDialog: openDialog.trigger,
    closeDialog: closeDialog.trigger,
  };

  siteSection = 'Teaching Resources';
  locationSearch: string;
  isMobileViewToolbar = false;
  isMobileUserAgent = false;

  get seo(): EdexSEO {
    return null;
  }

  get seoProps(): {
    title: string;
    description: string;
    image: string;
    noIndex: boolean;
  } {
    const { resource } = this;
    if (!resource) {
      return null;
    }
    const { settings } = resource;
    return {
      title: formatDocumentTitle(unsafeHTMLtext(settings?.seo?.title || resource.title)),
      description: settings?.seo?.metaDescription || getResourceShortDescription(resource),
      image: resource.heroImage.url || '',
      noIndex: settings?.seo?.noIndex,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.resizer);
    this.resizer();
  }

  disconnectedCallback(): void {
    window.removeEventListener('resize', this.resizer);
    super.disconnectedCallback();
  }

  resizer = () => {
    if (window.matchMedia('(max-width: 600px)').matches) {
      this.isMobileViewToolbar = true;
    }
    this.isMobileUserAgent = isNativeShareApplicable();
  };

  stateChanged(state: EdexState) {
    super.stateChanged(state);
    if (!this.builderPreview) {
      this.resource = selectResource(state);
      this.isResourceLoading = selectResourceLoading(state);
      this.error = selectResourceError(state);
      this.locationSearch = selectLocationSearch(state);
      if (!isEmpty(this.resource) && this.isResourceLoading) {
        const products = this.getProducts(this.resource);
        const hasCCXTemplate = this.hasExpressTemplate(this.resource);
        const payload = {
          isAdobeForEducation_bool: this.resource.author.id === ADOBE_FOR_EDUCATION_ID,
          haveAdobeExpress_bool: products.has('express'),
          haveEditableExpressTemplate_bool: hasCCXTemplate,
          havePhotoshop_bool: products.has('photoshop'),
          subject: this.resource.subjects.primary?.urlLabel || '',
          academicLevel: this.resource.academicLevels.primary?.urlLabel || '',
        };
        FullStoryUtils.getInstance().event(AnalyticsEvent.resourceVisited, payload);
      }
    }
    this.dialog = selectDialog(state);
  }

  getProducts(resource: MarshalledResource) {
    const products: Set<string> = new Set();
    products.add(resource.products.primary?.vanityURL);
    resource.products.secondary?.forEach((product) => {
      products.add(product.vanityURL);
    });
    return products;
  }

  hasExpressTemplate(resource: MarshalledResource) {
    return resource.links?.web
      ? resource.links.web.some((link) =>
          isCCXTemplateOrProjectUrl({
            url: link.url,
            components: link.components,
            isLinkFromAssetBrowser: link.metadata?.isLinkFromAssetBrowser,
            isLinkFromProjectXAssetBrowser: link.metadata?.isLinkFromProjectXAssetBrowser,
            isProjectXRemixLink: link.metadata?.isLinkFromAssetBrowser,
          })
        )
      : false;
  }

  get isLoading(): boolean {
    return this.isResourceLoading;
  }

  get isNotFound() {
    return this.error?.response?.status === 404;
  }

  get assetCount() {
    return this.resource ? getResourceAssetCount(this.resource) : 0;
  }

  get canFetchRelatedResources(): boolean {
    return !!(this.resource && this.selfIsFulfilled && !this.resource.relatedResources);
  }

  get isReplaceStateRequired(): boolean {
    const vanityURL = this.vanityURL || (router.location?.params?.vanityURL as string);
    if (!vanityURL) {
      return false;
    }
    return this.resource && this.resource.SEOUrl && this.resource.vanityURL != vanityURL;
  }

  async firstUpdated() {
    await this.fetchResource();
  }

  async fetchResource() {
    const vanityURL = this.vanityURL || (router.location?.params?.vanityURL as string);
    if (this.builderPreview || !vanityURL || (this.resource && this.resource.vanityURL === vanityURL)) {
      return;
    }
    this.actions.fetchResource({ id: vanityURL });
  }

  async updated(changedProperties) {
    const resourceHasChanged = !!(
      !!this.resource &&
      changedProperties.has('resource') &&
      changedProperties.get('resource')?.id !== this.resource.id
    );

    /**
     * track pageview: trigger if resource id has changed
     */
    if (resourceHasChanged) {
      this.track({
        event: this.analyticsViewEvent,
        payload: { id: this.resource.id },
      });
      processSEO(this.seo);
    }

    if (this.resource && this.selfIsFulfilled && this.postAuth) {
      if (this.isGuest) {
        this.removePostAuth();
      } else {
        await this.onPostAuthDetected(this.postAuth);
      }
    }
  }

  /***
   * Post authentication action detected - process and remove
   */
  private async onPostAuthDetected(postAuth: PostAuth) {
    this.removePostAuth();
    await this.processPostAuth(postAuth);
  }

  /**
   * Currently common for resource details/preview.
   * Override in child class when needed.
   */
  async processPostAuth({ actid, dialog }: PostAuth) {
    const timeout = IS_TEST ? 0 : 500; // slight delay for a better experience
    await delay(timeout);
    if (dialog) {
      this.actions.openDialog(dialog);
    } else if (actid) {
      switch (actid) {
        case Actid.saveResource:
          this.actions.saveResource({
            id: this.resource.id,
          });
          break;
        default:
          break;
      }
    }
  }

  fetchRelatedResources = () => {
    const {
      canFetchRelatedResources,
      resource: { id },
    } = this;
    if (!canFetchRelatedResources) {
      return;
    }

    const payload: FetchRelatedResourcesPayload = { id };

    // api requires these
    if (id) {
      this.actions.fetchRelatedResource(payload);
    }
  };

  isDialogOpen(id: EdexDomID) {
    return this.dialog?.id === id;
  }

  handleRelatedClick = (relatedResourceID: string) => () => {
    if (this.isDialogOpen(EdexDomID.resourceExportedDialog)) {
      this.actions.closeDialog();
    }
    this.track({
      event: AnalyticsEvent.resourceClickRelatedResource,
      payload: { id: this.resource.id, relatedResourceID },
    });
    Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.relatedResourceClick, 'Related resource click');
  };

  renderCardBlocker() {
    return this.builderPreview
      ? html`
          <div class="card-blocker"></div>
        `
      : null;
  }

  renderTitle = () => {
    const {
      resource: { title },
    } = this;

    return html`
      <h1 class="resource-details-title">${unsafeHTML(title)}</h1>
    `;
  };

  emitDriveViewEvent(exportType: ExportType) {
    const isGd = exportType === ExportType.googleDrive,
      isOd = exportType === ExportType.oneDrive;
    if (isGd || isOd) {
      Analytics.getInstance().emitEvent(
        isGd ? AdobeAnalyticsEvent.resourceViewGoogle : AdobeAnalyticsEvent.resourceViewOneDrive,
        `View in ${isGd ? 'Google' : 'One'} Drive click`
      );
    }
  }

  renderHelpfulInfo = (isHeading: boolean, items: ProductHelpfulInfo[]) => {
    return html`
      <div class="resource-helpful-info-container-new">
        <h2 class=${isHeading ? 'body-header' : 'helpful-title'}>${$t(I18nKey.helpfulResources)}</h2>

        <div class="new-resource-helpful-info-cards">
          ${items.map(
            ({ title, imageURL, description, url }) => html`
            <a href="${url}" target="_blank">
              <div class="resource-helpful-info-card">
                    <img
                      src="${getResizedImageURL(imageURL, HELPFUL_INFORMATION_IMAGE_WIDTH)}"
                      alt="${title}"
                      loading="lazy"
                    />
                <div class="resource-helpful-info-description">
                  <h3>${title}</h3>
                  <p>${description}</div>
                </div>
              </div>
            </a>
            `
          )}
        </div>
        ${this.renderCardBlocker()}
      </div>
    `;
  };

  renderToolbar(id: EdexDomID) {
    return this.builderPreview
      ? null
      : html`
          <edex-resource-header-toolbar
            id="${id}"
            .renderDialogs="${id === EdexDomID.desktopToolbar}"
            .resource="${this.resource}"
            .locationSearch=${this.locationSearch}
          ></edex-resource-header-toolbar>
        `;
  }

  renderMain() {
    throw new Error('Not Implemented');
  }

  /**
   * Common between details/preview
   */
  private renderCommon() {
    return html`
      <div class="mobile-device-only">
        ${this.isMobileViewToolbar
          ? html`
              <edex-resource-header-toolbar
                .isMobileView=${this.isMobileViewToolbar}
                .locationSearch=${this.locationSearch}
                .isMobileUserAgent=${this.isMobileUserAgent}
              ></edex-resource-header-toolbar>
            `
          : null}
      </div>
      <edex-resource-action-bar></edex-resource-action-bar>
    `;
  }

  render() {
    const { isLoading, isNotFound, resource } = this;
    if (isLoading) {
      return html`
        <edex-loader></edex-loader>
      `;
    } else if (isNotFound) {
      return html`
        <edex-not-found></edex-not-found>
      `;
    } else if (resource) {
      return html`
        ${this.renderMain()} ${this.renderCommon()}
      `;
    } else {
      // fallback to loader, eg in case of 500 error
      return html`
        <edex-loader></edex-loader>
      `;
    }
  }
}
